import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable, Subject, defer, firstValueFrom, interval, of } from 'rxjs';
import { catchError, concatMap, map, take, takeUntil, tap } from 'rxjs/operators';
import { RequestRoot, FeaturesFactory, UTCDate } from 'types';
import { RequestDataAccessService } from 'data-access';
import { ValidationError } from 'validation';
import {
    AppContext,
    Command,
    CommandContext,
    CommandPage,
    CommandSideEffects,
    CommandWrapper,
    Features,
    FeaturesI18N,
    TableEditorDialogBoxRef,
    TableEditorDialogBoxData,
    ValidationBuilder,
    ValidationService,
    WizardContext,
} from 'controls';
import { AppService } from 'src/app/app.service';
import { NavigationService } from 'src/app/service/navigation.service';
import { DialogResult, DialogService } from 'src/app/service/dialog.service';
import { ActivityCommand, NavigationBackHandler } from 'src/app/model/activity';
import { SaveCommand } from 'src/app/command/save-command';
import { ReadyCommand } from 'src/app/command/ready-command';
import { Department, DeptBookService } from 'src/app/service/deptbook.service';
import { RequestFeatures } from 'src/webid/4.0/model/features';
import { RequestFeaturesV2 } from 'src/webid/4.0/model/features2';
import { importFromManual } from 'src/webid/4.0/model/import';
import { InternetConnectionState } from 'src/app/model/internet-connection-state';
import { NetworkService } from 'src/app/service/network.service';

@Component({
    selector: 'request-editor',
    templateUrl: './request-editor.component.html',
    styleUrls: ['./request-editor.component.scss'],
    providers: [{ provide: ValidationService, useClass: ValidationService }],
})
export class RequestEditorComponent extends CommandPage implements OnInit, OnDestroy {
    public v1: boolean;
    public v2: boolean;
    public isCommandInProgress: boolean = false;
    public currentNavigationBackHandler: NavigationBackHandler | null = null;
    public request: RequestRoot | null = null;
    public department: Department | null = null;
    public saveOnlyCommand: Command<RequestRoot> | null = null;
    public saveAndExitCommand: Command<RequestRoot> | null = null;
    public readyAndExitCommand: Command<RequestRoot> | null = null;
    public initialized: Subject<void> = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private appService: AppService,
        private appContext: AppContext,
        private cmdContext: CommandContext,
        private dialogService: DialogService,
        private deptBookService: DeptBookService,
        private validationService: ValidationService,
        private navigationService: NavigationService,
        private requestService: RequestDataAccessService,
        private tableEditorRef: TableEditorDialogBoxRef,
        private featuresFactory: FeaturesFactory,
        private wizardContext: WizardContext,
        private networkService: NetworkService
    ) {
        super();
    }

    ngOnInit(): void {
        this.setupInitialActivitySettings();
        this.setupCommonActivitySettings();
        this.tableEditorRef.opened$.pipe(takeUntil(this.getDead$())).subscribe((d) => this.setupDialogActivitySettings(d));
        this.tableEditorRef.closed$.pipe(takeUntil(this.getDead$())).subscribe(() => this.setupCommonActivitySettings());
        this.route.queryParams.subscribe((params) => {
            this.initialize(params);
        });
        interval(250)
            .pipe(
                takeUntil(this.initialized),
                tap((n) => {
                    this.updateViewState();
                })
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.setDead();
        this.appService.currentFeaturesReference = null;
        this.wizardContext.getDataURLs().forEach((x) => URL.revokeObjectURL(x));
    }

    reloadDepartmentInfo(): void {
        if (this.request) {
            let code = this.getRequestDepartmentCode(this.request);
            this.executeDepartmentReload(code).subscribe();
        }
    }

    private initialize(params: Params): void {
        let requestId = params['id'];
        let departmentCode = params['department'];

        const requestRootBuffer = new RequestRoot();
        requestRootBuffer.id = requestId;

        let r: RequestRoot | null = null;
        let loading$ = this.requestService.load(requestRootBuffer).pipe(
            tap((request) => (r = request)),
            concatMap((r) => this.executeDepartmentReload(this.getRequestDepartmentCode(r))),
            tap((d) => {
                if (r && d) {
                    this.setupCountryAndCity(r, d);
                }
            }),
            map((d) => r)
        );

        let creation$ = this.executeDepartmentReload(departmentCode).pipe(
            concatMap(() => this.requestService.createUID()),
            concatMap((uid) => this.createRequest(uid))
        );

        let request$ = requestId ? loading$ : creation$;
        request$.pipe(takeUntil(this.getDead$())).subscribe({
            next: (request) => {
                this.request = request;
                this.v1 = request?.features instanceof RequestFeatures;
                this.v2 = request?.features instanceof RequestFeaturesV2;

                this.appService.currentFeaturesReference = this.request?.features.constructor;

                Features.updateFieldsVisibility(this.request?.features, this.featuresFactory.createFromObject(this.request?.features));

                this.appService.draft$.pipe(takeUntil(this.getDead$())).subscribe((_) => this.setDirty(true));
                this.validationService.initialize(() => this.validate());

                let date$ = defer(() => of(new Date()));
                this.saveOnlyCommand = new SaveCommand(date$, false);
                this.saveAndExitCommand = new SaveCommand(date$, true);
                this.readyAndExitCommand = new ReadyCommand(date$);
                this.getCommandCompleted$().subscribe((r: CommandSideEffects) => {
                    this.onCommandCompleted(r);
                });

                this.wizardContext.setRequestFeaturesTemplate(this.featuresFactory.createFromObject(this.request?.features));
                setTimeout(() => {
                    this.wizardContext.getTabs().forEach((t) =>
                        setTimeout(() => {
                            t.activate(true);
                        }, 1)
                    );
                }, 1);
            },
            error: () => {
                this.navigationService.activity.loader.hide();
                this.navigationService.navigateBack();
            },
        });
    }

    private setupCountryAndCity(request: RequestRoot, department: Department): void {
        if (department) {
            let features: RequestFeatures | RequestFeaturesV2 = request.features;
            features.mobileOptions.departmentCountry = department.country;
            features.mobileOptions.departmentCity = department.city;
        }
    }

    async canDeactivate(currentUrl: string): Promise<boolean> {
        if (this.isCommandInProgress || !this.isDirty()) {
            return true;
        }

        const result = await firstValueFrom(this.dialogService.confirmSaveAction());

        switch (result) {
            case DialogResult.CANCEL:
                history.pushState({}, '', currentUrl);
                return false;
            case DialogResult.YES:
                if (this.saveAndExitCommand) {
                    this.executeCommand(this.saveAndExitCommand);
                }
                return true;
            case DialogResult.NO:
                return true;
        }
    }

    private onCommandCompleted(result: CommandSideEffects): void {
        this.isCommandInProgress = false;

        let succeeded = true;
        if (result.validationTriggered) {
            if (result.validationTriggered.hasERROR()) {
                succeeded = false;
                const caption = 'Внимание';
                const message = 'В заявлении найдены ошибки. Заявление сохранено в черновик.';
                this.dialogService.notifyInMessageBox(caption, message);
            }
            this.validationService.finalize();
        }

        if (this.currentNavigationBackHandler) {
            if (succeeded) {
                this.currentNavigationBackHandler();
            }
            this.currentNavigationBackHandler = null;
        }
    }

    private executeCommand(command: Command<RequestRoot>): void {
        if (this.request) {
            this.isCommandInProgress = true;
            let wrapper = new CommandWrapper(this.appContext, this.cmdContext, this, command);
            wrapper.execute([this.request]);
        }
    }

    private executeDepartmentReload(code: string): Observable<Department | null> {
        return this.networkService.internet$.pipe(
            take(1),
            concatMap((state) => (state === InternetConnectionState.OFFLINE ? of<Department[] | null>(null) : this.getDepartmentList())),
            map((list) => (!!list ? list.find((d) => d.code === code) ?? null : null)),
            tap((dept) => (this.department = dept))
        );
    }

    private getDepartmentList(): Observable<Department[] | null> {
        if (this.appService.departments) {
            return of(this.appService.departments);
        }
        return this.deptBookService.list().pipe(
            tap((list) => (this.appService.departments = list)),
            catchError(() => of(null))
        );
    }

    private createRequest(uid: string): Observable<RequestRoot> {
        //* код Калининград '03901'
        return defer(() => {
            let featuresReference = RequestFeaturesV2;
            let request = RequestRoot.init(uid, featuresReference);
            let features: RequestFeatures | RequestFeaturesV2 = request.features;
            features.mobileOptions.department = this.department?.code ?? '';
            features.mobileOptions.departmentCountry = this.department?.country ?? '';
            features.mobileOptions.departmentCity = this.department?.city ?? '';
            features.mobileOptions.departmentUsesInternetApps = false;
            features.mobileOptions.departmentUsesPassportMail = Boolean(this.department?.canSendPassportByMail);
            importFromManual(request);

            return of(request);
        });
    }

    private getRequestDepartmentCode(request: RequestRoot) {
        let features: RequestFeatures | RequestFeaturesV2 = request.features;
        return features.mobileOptions.department;
    }

    private setupInitialActivitySettings() {
        this.navigationService.activity.reset();
        this.navigationService.activity.loader.show('Инициализация страницы');
    }

    private setupCommonActivitySettings(): void {
        this.navigationService.activity.toolbar.setTitle('Заявление');
        this.navigationService.activity.toolbar.setOptions(this.createPrimaryCommands());
        this.navigationService.activity.onBack = null;
    }

    private setupDialogActivitySettings(data: TableEditorDialogBoxData): void {
        this.navigationService.activity.toolbar.setTitle(FeaturesI18N.getString(data.title));
        this.navigationService.activity.toolbar.setOptions(this.createTableDialogCommands());
        this.navigationService.activity.onBack = (dh) => {
            this.tableEditorRef.cancel();
        };
    }

    private createPrimaryCommands(): ActivityCommand[] {
        let always = () => true;
        let isDirty = () => this.isDirty();
        let save = () => {
            if (this.saveOnlyCommand) {
                this.executeCommand(this.saveOnlyCommand);
            }
        };
        let done = () => {
            if (this.readyAndExitCommand) {
                this.executeCommand(this.readyAndExitCommand);
            }
        };
        return [
            {
                icon: 'save',
                title: 'Сохранить',
                action: save,
                enabled: isDirty,
            },
            { icon: 'done', title: 'Готово', action: done, enabled: always },
        ];
    }

    private createTableDialogCommands(): ActivityCommand[] {
        let submit = () => this.tableEditorRef.submit();
        return [
            {
                icon: 'done',
                title: 'Готово',
                action: submit,
                enabled: () => true,
            },
        ];
    }

    private updateViewState(): void {
        let elements = document.getElementsByTagName('i-table-editor');
        if (elements.length > 0) {
            this.navigationService.activity.loader.hide();
            this.initialized.next();
            this.initialized.complete();
        }
    }

    private validate(): ValidationError[] {
        let validator = new ValidationBuilder(this.appContext, this.request?.features);
        let result = validator.validate(this.request?.features, UTCDate.today(new Date()));
        return result.getErrors();
    }
}
